import { useMemo } from 'react';
import {
  IAuditErrorCategoryInput,
  generateFieldIdOptions,
} from '@scale/shared/types/auditErrorCategories';
import { useUnleashFeatureFlagWithLoading } from 'react-scale/hooks/useUnleashFeatureFlag';
import { useProjectDefaultErrorCategories } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';

export const getDefaultErrorCategory = (
  projectId: string,
  userEmail: string,
): IAuditErrorCategoryInput => ({
  fieldId: '',
  dimension: '',
  errorCategory: '',
  description: '',
  maxScore: 5,
  projectId,
  updatedBy: userEmail,
});

export default function useErrorCategoryOptions(projectId: string, fieldIds: string[]) {
  const {
    data: showFieldIdsOptionsFromTemplateParams,
    isLoading: isLoadingShowFieldIdsOptionsFromTemplateParams,
  } = useUnleashFeatureFlagWithLoading(
    'show-field-id-options-from-template-params',
    { projectId },
    false,
  );
  const {
    data: showFieldIdsOptionsFromDefaultErrorCategories,
    isLoading: isLoadingShowFieldIdsOptionsFromDefaultErrorCategories,
  } = useUnleashFeatureFlagWithLoading(
    'show-field-id-options-from-default-error-categories',
    { projectId },
    false,
  );

  const { data: projectDefaultErrorCategories, isLoading: isLoadingProjectDefaultErrorCategories } =
    useProjectDefaultErrorCategories(projectId);
  const distinctFieldIds = useMemo(
    () => Array.from(new Set(projectDefaultErrorCategories?.map(cat => cat.fieldId) ?? [])),
    [projectDefaultErrorCategories],
  );

  const fieldIdOptions = generateFieldIdOptions(
    fieldIds ?? [],
    distinctFieldIds,
    showFieldIdsOptionsFromTemplateParams,
    showFieldIdsOptionsFromDefaultErrorCategories,
  );

  const distinctDimensions = Array.from(
    new Set(projectDefaultErrorCategories?.map(cat => cat.dimension) ?? []),
  );

  const isLoading =
    isLoadingShowFieldIdsOptionsFromTemplateParams ||
    isLoadingShowFieldIdsOptionsFromDefaultErrorCategories ||
    isLoadingProjectDefaultErrorCategories;

  return {
    fieldIdOptions,
    distinctDimensions,
    isLoading,
  };
}
